import {
  notifyCreditExhaustion,
} from "@tribunalea/ui/credit-exhaustion";

function apiError(payload, status) {
  notifyCreditExhaustion(payload);
  const error = new Error(
    payload?.error || payload?.message || "No pudimos completar la operación.",
  );
  error.code = payload?.code || "request_failed";
  error.status = status;
  return error;
}

export function parseSseBlock(block) {
  let event = "message";
  const dataLines = [];

  for (const line of String(block || "").split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
  }
  if (!dataLines.length) return null;

  const rawData = dataLines.join("\n");
  let data = rawData;
  try {
    data = JSON.parse(rawData);
  } catch {
    // SSE also permits plain-text data.
  }
  return { event, data };
}

export async function consumeSseStream(response, onEvent) {
  if (!response.body) {
    throw new Error("El navegador no permite recibir la respuesta en tiempo real.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    buffer = buffer.replace(/\r\n/g, "\n");

    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const parsed = parseSseBlock(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      if (parsed) {
        notifyCreditExhaustion(parsed.data);
        onEvent(parsed.event, parsed.data);
      }
      boundary = buffer.indexOf("\n\n");
    }
    if (done) break;
  }

  const parsed = parseSseBlock(buffer);
  if (parsed) {
    notifyCreditExhaustion(parsed.data);
    onEvent(parsed.event, parsed.data);
  }
}

export function createJustinianoClient({
  apiUrl = "http://localhost:4000",
  appId,
  apiBasePath,
  conversationTitle = "Conversación con Justiniano",
  conversationScope = null,
  getAccessToken,
  surface = "floating_assistant",
  conversationMetadata = null,
  fetchImplementation = fetch,
}) {
  const basePath = apiBasePath
    || `/api/mentis/v1/apps/${encodeURIComponent(appId)}`;

  async function headers(includeJson = true) {
    const token = getAccessToken ? await getAccessToken() : null;
    return {
      ...(includeJson ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    };
  }

  async function request(path, options = {}) {
    const response = await fetchImplementation(`${apiUrl}${basePath}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        ...(await headers(options.body !== undefined)),
        ...options.headers,
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw apiError(payload, response.status);
    return payload.data;
  }

  async function globalRequest(path) {
    const response = await fetchImplementation(`${apiUrl}${path}`, {
      credentials: "include",
      headers: await headers(false),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw apiError(payload, response.status);
    return payload.data;
  }

  return {
    getProfile() {
      return globalRequest("/api/profile");
    },

    getPageHelp(route) {
      return request(`/help?route=${encodeURIComponent(route || "/")}`);
    },

    listConversations(options = {}) {
      return request("/conversations", options);
    },

    listConversationHistory({
      limit = 30,
      cursor,
      query,
      assignedOnly = false,
      contextType,
      contextId,
      signal,
    } = {}) {
      const parameters = new URLSearchParams({ limit: String(limit) });
      if (cursor) parameters.set("cursor", cursor);
      if (query) parameters.set("query", query);
      if (assignedOnly) parameters.set("assignedOnly", "true");
      if (conversationScope) parameters.set("scope", conversationScope);
      if (contextType) parameters.set("contextType", contextType);
      if (contextId) parameters.set("contextId", contextId);
      return request(`/conversations/history?${parameters}`, { signal });
    },

    loadConversation(conversationId, options = {}) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}`,
        options,
      );
    },

    createConversation() {
      return request("/conversations", {
        method: "POST",
        body: JSON.stringify({
          title: conversationTitle,
          metadata: {
            surface,
            ...(conversationScope ? { conversationScope } : {}),
            ...(conversationMetadata || {}),
          },
        }),
      });
    },

    assignConversation(conversationId, assignment) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/assignment`,
        {
          method: "PUT",
          body: JSON.stringify(assignment),
        },
      );
    },

    removeConversationAssignment(conversationId) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/assignment`,
        { method: "DELETE" },
      );
    },

    async sendMessage(
      conversationId,
      content,
      onEvent,
      {
        signal,
        appContext,
        responseMode,
        attachmentIds = [],
        notebookIds = [],
        notebookScope = "selected",
        requestedSources = [],
        draftContext = null,
      } = {},
    ) {
      const response = await fetchImplementation(
        `${apiUrl}${basePath}/conversations/${encodeURIComponent(conversationId)}/messages`,
        {
          method: "POST",
          credentials: "include",
          headers: await headers(true),
          body: JSON.stringify({
            content,
            ...(appContext ? { appContext } : {}),
            ...(responseMode ? { responseMode } : {}),
            ...(attachmentIds.length ? { attachmentIds } : {}),
            ...(notebookIds.length ? { notebookIds } : {}),
            ...(notebookScope === "all" ? { notebookScope } : {}),
            ...(requestedSources.length ? { requestedSources } : {}),
            ...(draftContext ? { draftContext } : {}),
          }),
          signal,
        },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw apiError(payload, response.status);
      }
      await consumeSseStream(response, onEvent);
    },

    editDraft(conversationId, input, { appContext } = {}) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/draft-edits`,
        {
          method: "POST",
          body: JSON.stringify({
            ...input,
            ...(appContext ? { appContext } : {}),
          }),
        },
      );
    },

    async uploadAttachment(conversationId, file) {
      const body = new FormData();
      body.append("file", file);
      const response = await fetchImplementation(
        `${apiUrl}${basePath}/conversations/${encodeURIComponent(conversationId)}/attachments`,
        {
          method: "POST",
          credentials: "include",
          headers: await headers(false),
          body,
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw apiError(payload, response.status);
      return payload.data;
    },

    deleteAttachment(conversationId, attachmentId) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { method: "DELETE" },
      );
    },

    confirmAction(conversationId, actionId) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/actions/${encodeURIComponent(actionId)}/confirm`,
        { method: "POST" },
      );
    },

    cancelAction(conversationId, actionId) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/actions/${encodeURIComponent(actionId)}/cancel`,
        { method: "POST" },
      );
    },
  };
}
